import React, { useRef, useState, useCallback } from "react";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    useLocation,
    useNavigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { TerminalHeader } from "./components/TerminalHeader";
import { StatusBar } from "./components/StatusBar";
import { CommandPalette } from "./components/CommandPalette";
import { FollowingCat } from "./components/FollowingCat";
import { About } from "./pages/About";
import { Posts } from "./pages/Posts";
import { Post } from "./pages/Post";
import { NotFound } from "./pages/NotFound";
import { useVimBindings } from "./hooks/useVimBindings";
import { useKonami } from "./hooks/useKonami";
import "./styles/global.css";
import "./App.css";

// Global config for optional effects
window.MUSTARDTIGER_CONFIG = {
    enableCRT: true, // Set to true to enable CRT effects
};

function AppContent() {
    const scrollRef = useRef(null);
    const [searchActive, setSearchActive] = useState(false);
    const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
    const [zenMode, setZenMode] = useState(false);
    const [catVisible, setCatVisible] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const handleSearch = useCallback(() => {
        if (location.pathname === "/posts") {
            // Already on posts page, activate search
            setSearchActive(true);
        } else {
            // Navigate to posts page, then activate search
            navigate("/posts");
            // Delay to allow navigation to complete
            setTimeout(() => setSearchActive(true), 100);
        }
    }, [location.pathname, navigate]);

    const handleSearchClose = useCallback(() => {
        setSearchActive(false);
    }, []);

    const handleOpenCommandPalette = useCallback(() => {
        setCommandPaletteOpen(true);
    }, []);

    const handleCloseCommandPalette = useCallback(() => {
        setCommandPaletteOpen(false);
    }, []);

    const handleZenToggle = useCallback(() => {
        setZenMode((prev) => !prev);
    }, []);

    const handleShowCat = useCallback(() => {
        setCatVisible(true);
    }, []);

    const handleHideCat = useCallback(() => {
        setCatVisible(false);
    }, []);

    // Konami code easter egg
    useKonami(() => {
        alert(
            "🎮 Konami Code Activated! 🎮\n\nYou found the secret! Implement your easter egg here.",
        );
    });

    // Vim keybindings
    useVimBindings(scrollRef, handleSearch, handleOpenCommandPalette);

    return (
        <div
            className={`app ${window.MUSTARDTIGER_CONFIG.enableCRT ? "crt-effect" : ""} ${zenMode ? "zen-mode" : ""}`}
        >
            {!zenMode && <TerminalHeader />}
            <main className="main-content" ref={scrollRef}>
                <Routes>
                    <Route path="/" element={<About />} />
                    <Route
                        path="/posts"
                        element={
                            <Posts
                                searchActive={searchActive}
                                onSearchClose={handleSearchClose}
                            />
                        }
                    />
                    <Route path="/posts/:id" element={<Post />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </main>
            {!zenMode && <StatusBar scrollRef={scrollRef} />}
            <CommandPalette
                isOpen={commandPaletteOpen}
                onClose={handleCloseCommandPalette}
                onZenToggle={handleZenToggle}
                onShowCat={handleShowCat}
            />
            {catVisible && <FollowingCat onDismiss={handleHideCat} />}
        </div>
    );
}

function App() {
    return (
        <HelmetProvider>
            <Router>
                <AppContent />
            </Router>
        </HelmetProvider>
    );
}

export default App;
